import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PartnerEnrollment {
  id: string;
  user_id: string;
  status: string;
  start_date: string;
  end_date: string;
  feedback_required_per_month: number;
}

interface PartnerFeedback {
  id: string;
  category: string;
  title: string;
  description: string;
  screenshot_url: string | null;
  priority: string;
  page_url: string | null;
  status: string;
  created_at: string;
}

export const usePartnerProgram = () => {
  const [enrollment, setEnrollment] = useState<PartnerEnrollment | null>(null);
  const [feedbackThisMonth, setFeedbackThisMonth] = useState<PartnerFeedback[]>([]);
  const [allFeedback, setAllFeedback] = useState<PartnerFeedback[]>([]);
  const [isPartner, setIsPartner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartnerStatus();
  }, []);

  const fetchPartnerStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: enrollmentData } = await (supabase as any)
        .from("partner_enrollments")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "active")
        .maybeSingle();

      if (enrollmentData) {
        setEnrollment(enrollmentData);
        setIsPartner(true);

        // Get feedback for current month
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);

        const { data: monthFeedback } = await (supabase as any)
          .from("partner_feedback_safe")
          .select("*")
          .eq("user_id", user.id)
          .gte("created_at", startOfMonth.toISOString())
          .order("created_at", { ascending: false });

        setFeedbackThisMonth(monthFeedback || []);

        const { data: allFb } = await (supabase as any)
          .from("partner_feedback_safe")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        setAllFeedback(allFb || []);
      }
    } catch (error) {
      console.error("Error fetching partner status:", error);
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async (feedback: {
    category: string;
    title: string;
    description: string;
    priority: string;
    page_url?: string;
    screenshot_url?: string;
  }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { data, error } = await (supabase as any)
      .from("partner_feedback")
      .insert({ ...feedback, user_id: user.id })
      .select()
      .single();

    if (error) throw error;

    // Notify admin about new feedback
    supabase.functions.invoke("admin-notify", {
      body: {
        event: "feedback_submitted",
        title: `Partner Feedback: ${feedback.title}`,
        details: {
          category: feedback.category,
          priority: feedback.priority,
          title: feedback.title,
          description: feedback.description.slice(0, 200),
        },
        urgency: feedback.priority === "critical" ? "high" : "medium",
      },
    }).catch(() => {}); // fire-and-forget

    await fetchPartnerStatus();
    return data;
  };

  const uploadScreenshot = async (file: File): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const ext = file.name.split(".").pop();
    const path = `${user.id}/${Date.now()}.${ext}`;

    const { error } = await supabase.storage
      .from("partner-feedback")
      .upload(path, file);

    if (error) throw error;

    // Get signed URL
    const { data: signedData } = await supabase.functions.invoke("get-signed-url", {
      body: { bucket: "partner-feedback", path },
    });

    return signedData?.signedUrl || path;
  };

  const feedbackRequired = enrollment?.feedback_required_per_month || 5;
  const feedbackSubmitted = feedbackThisMonth.length;
  const feedbackRemaining = Math.max(0, feedbackRequired - feedbackSubmitted);

  return {
    isPartner,
    enrollment,
    feedbackThisMonth,
    allFeedback,
    feedbackRequired,
    feedbackSubmitted,
    feedbackRemaining,
    loading,
    submitFeedback,
    uploadScreenshot,
    refetch: fetchPartnerStatus,
  };
};
